import {
  getAnalytics,
  isSupported,
  logEvent as firebaseLogEvent,
  setCurrentScreen,
  setUserId,
  type Analytics,
} from 'firebase/analytics';

import { analyticsConfig } from '@/src/config/env';
import { firebaseApp } from '@/src/lib/firebase';

type AnalyticsParams = Record<
  string,
  string | number | boolean | null | undefined
>;

type MetaPixelFunction = (...args: unknown[]) => void;
type MetaEventOptions = {
  eventID?: string;
};

declare global {
  interface Window {
    fbq?: MetaPixelFunction;
  }
}

let analyticsPromise: Promise<Analytics | null> | null = null;
let skippedInitialMetaPageView = false;

const metaStandardEventByAppEvent: Record<string, string> = {
  sign_up: 'CompleteRegistration',
  user_search: 'Search',
};

const blockedMetaParamNamePatterns = [
  /email/i,
  /token/i,
  /secret/i,
  /password/i,
  /url/i,
  /host/i,
  /path/i,
  /text/i,
  /body/i,
  /name/i,
  /(^|_)uid($|_)/i,
  /(^|_)id($|_)/i,
  /^firebase_screen/i,
];

function getAnalyticsInstance() {
  if (!analyticsConfig.measurementId) {
    return Promise.resolve(null);
  }

  analyticsPromise ??= isSupported()
    .then((supported) => (supported ? getAnalytics(firebaseApp) : null))
    .catch((error) => {
      console.warn('Firebase Analytics is not available.', error);
      return null;
    });

  return analyticsPromise;
}

function cleanParams(params: AnalyticsParams | undefined) {
  if (!params) {
    return undefined;
  }

  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined),
  );
}

function getMetaPixel() {
  if (typeof window === 'undefined' || typeof window.fbq !== 'function') {
    return null;
  }

  return window.fbq;
}

function cleanMetaParams(params: AnalyticsParams | undefined) {
  if (!params) {
    return undefined;
  }

  const cleanEntries = Object.entries(params)
    .filter(([key, value]) => {
      if (value === null || value === undefined) {
        return false;
      }

      return (
        !blockedMetaParamNamePatterns.some((pattern) => pattern.test(key)) &&
        key !== 'meta_event_id'
      );
    })
    .map(([key, value]) => [
      key,
      typeof value === 'string' ? value.slice(0, 100) : value,
    ]);

  if (cleanEntries.length === 0) {
    return undefined;
  }

  return Object.fromEntries(cleanEntries);
}

function metaEventOptions(params: AnalyticsParams | undefined) {
  const eventId = params?.meta_event_id;

  if (typeof eventId !== 'string' || eventId.trim().length === 0) {
    return undefined;
  }

  return { eventID: eventId.trim().slice(0, 120) } satisfies MetaEventOptions;
}

function trackMetaPixelEvent(name: string, params?: AnalyticsParams) {
  const fbq = getMetaPixel();

  if (!fbq) {
    return;
  }

  const clean = cleanMetaParams(params);
  const options = metaEventOptions(params);

  try {
    const standardEventName = metaStandardEventByAppEvent[name];

    if (standardEventName) {
      fbq('track', standardEventName, clean, options);
    }

    fbq('trackCustom', name, clean, options);
  } catch (error) {
    console.warn('Meta Pixel event tracking failed.', error);
  }
}

function trackMetaPageView() {
  const fbq = getMetaPixel();

  if (!fbq) {
    return;
  }

  try {
    fbq('track', 'PageView');
  } catch (error) {
    console.warn('Meta Pixel page view tracking failed.', error);
  }
}

export function trackEvent(name: string, params?: AnalyticsParams) {
  trackMetaPixelEvent(name, params);

  void getAnalyticsInstance().then((analytics) => {
    if (!analytics) {
      return;
    }

    firebaseLogEvent(analytics, name, cleanParams(params));
  });
}

export function trackScreenView(pathname: string) {
  if (skippedInitialMetaPageView) {
    trackMetaPageView();
  } else {
    skippedInitialMetaPageView = true;
  }

  void getAnalyticsInstance().then((analytics) => {
    if (!analytics) {
      return;
    }

    setCurrentScreen(analytics, pathname);
    firebaseLogEvent(analytics, 'screen_view', {
      firebase_screen: pathname,
      firebase_screen_class: 'ExpoRouter',
    });
  });
}

export function setAnalyticsUserId(uid: string | null) {
  void getAnalyticsInstance().then((analytics) => {
    if (!analytics) {
      return;
    }

    setUserId(analytics, uid);
  });
}
